import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { isNumber } from 'src/util/math';

const PreviousButton = styled.button`
 border-top-right-radius: 0 !important;
 border-bottom-right-radius: 0 !important;
`;

const NextButton = styled.button`
 border-top-left-radius: 0 !important;
 border-bottom-left-radius: 0 !important;
`;

/**
 * Generates an array of page numbers to display in pagination
 * @param {number} currentPage - Current active page (1-based)
 * @param {number} totalPages - Total number of pages
 * @param {number} maxVisible - Maximum number of page buttons to show
 * @returns {Array<number|string>} Array of page numbers and ellipsis ('...')
 */
export const generatePageNumbers = (currentPage, totalPages, maxVisible = 7) => {
  if (!isNumber(currentPage) || !isNumber(totalPages)) {
    return [];
  }

  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = [];
  const halfVisible = Math.floor(maxVisible / 2);

  // Always show first page
  pages.push(1);

  let startPage = Math.max(2, currentPage - halfVisible);
  let endPage = Math.min(totalPages - 1, currentPage + halfVisible);

  // Adjust range if we're near the beginning or end
  if (currentPage <= halfVisible + 1) {
    endPage = Math.min(totalPages - 1, maxVisible - 1);
  } else if (currentPage >= totalPages - halfVisible) {
    startPage = Math.max(2, totalPages - maxVisible + 2);
  }

  // Add ellipsis after first page if needed
  if (startPage > 2) {
    pages.push('...');
  }

  // Add middle pages
  for (let i = startPage; i <= endPage; i += 1) {
    pages.push(i);
  }

  // Add ellipsis before last page if needed
  if (endPage < totalPages - 1) {
    pages.push('...');
  }

  // Always show last page (if more than 1 page)
  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
};

/**
 * Pagination component for displaying page navigation controls
 * @param {object} props
 * @param {number} props.currentPage - Current active page (1-based)
 * @param {number} props.totalPages - Total number of pages
 * @param {Function} props.onPageChange - Callback when page changes
 * @param {boolean} [props.disabled] - Whether pagination is disabled
 * @param {string} [props.ariaLabel] - Aria label for navigation
 * @returns {React.ReactElement} Pagination component
 */
function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  disabled = false,
  ariaLabel = undefined,
}) {
  const intl = useIntl();

  const previousLabel = intl.formatMessage({
    id: 'pagination.previous.label',
    defaultMessage: 'Previous',
  });

  const nextLabel = intl.formatMessage({
    id: 'pagination.next.label',
    defaultMessage: 'Next',
  });

  const defaultAriaLabel = intl.formatMessage({
    id: 'pagination.navigation.label',
    defaultMessage: 'Pagination navigation',
  });

  if (totalPages <= 1) {
    return null;
  }

  const pages = generatePageNumbers(currentPage, totalPages);
  const hasPrevious = currentPage > 1;
  const hasNext = currentPage < totalPages;

  /**
   * Handles page click events
   * @param {number|string} page - Page number or ellipsis string
   */
  const handlePageClick = (page) => {
    if (disabled || page === currentPage || page === '...') {
      return;
    }
    onPageChange(page);
  };

  const handlePreviousClick = () => {
    if (disabled || !hasPrevious) {
      return;
    }
    onPageChange(currentPage - 1);
  };

  const handleNextClick = () => {
    if (disabled || !hasNext) {
      return;
    }
    onPageChange(currentPage + 1);
  };

  return (
    <nav aria-label={ariaLabel || defaultAriaLabel}>
      <ul className="pagination" style={{ marginBottom: '0.5rem' }}>
        <li className={`page-item ${!hasPrevious || disabled ? 'disabled' : ''}`}>
          <PreviousButton
            type="button"
            className="page-link"
            onClick={handlePreviousClick}
            disabled={!hasPrevious || disabled}
            aria-label={previousLabel}
          >
            <span aria-hidden="true">&laquo;</span>
            <span className="sr-only">{previousLabel}</span>
          </PreviousButton>
        </li>

        {pages.map((page, index) => {
          if (page === '...') {
            // Create a stable key based on position relative to first/last page
            const ellipsisKey = index < pages.length / 2 ? 'ellipsis-start' : 'ellipsis-end';
            return (
              <li key={ellipsisKey} className="page-item">
                <span className="page-link pagination-ellipsis" style={{ borderRadius: 0 }}>...</span>
              </li>
            );
          }

          const isCurrentPage = page === currentPage;

          return (
            <li
              key={page}
              className={`page-item ${isCurrentPage ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
            >
              <button
                type="button"
                className="page-link"
                onClick={() => handlePageClick(page)}
                disabled={disabled}
                style={{ borderRadius: 0 }}
                aria-label={isCurrentPage
                  ? intl.formatMessage({ id: 'pagination.current_page.label', defaultMessage: 'Current page {page}' }, { page })
                  : intl.formatMessage({ id: 'pagination.page.label', defaultMessage: 'Page {page}' }, { page })}
                aria-current={isCurrentPage ? 'page' : undefined}
              >
                {page}
              </button>
            </li>
          );
        })}

        <li className={`page-item ${!hasNext || disabled ? 'disabled' : ''}`}>
          <NextButton
            type="button"
            className="page-link"
            onClick={handleNextClick}
            disabled={!hasNext || disabled}
            aria-label={nextLabel}
          >
            <span aria-hidden="true">&raquo;</span>
            <span className="sr-only">{nextLabel}</span>
          </NextButton>
        </li>
      </ul>
    </nav>
  );
}

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  ariaLabel: PropTypes.string,
};

export default Pagination;
